import React from "react";
import {useFormik} from "formik";
import * as yup from "yup";
import {useTranslation} from "react-i18next";
import {useCurrentUser} from "../../../auth/CurrentUserProvider";
import {useFeedbackTools} from "../../../components/FeedbackTools";

import MDLoadingButton from "../../../components/MDLoadingButton";
import WorkgroupsAutocomplete from "../../../components/inputs/WorkgroupsAutocomplete";
import GroupsAutocomplete from "../../../components/inputs/GroupAutocomplete";

import MDBox from "materialDashboard/components/MDBox";
import MDTypography from "materialDashboard/components/MDTypography";

import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import ConfirmEmailButton from "./ConfirmEmailButton";

const getFormikValues = (user) => {
    return {
        name: user.name,
        surname: user.surname,
        email: user.email,
        workgroups: user.workgroups,
        groups: user.groups,
        isActive: user.isActive,
        notes: user.notes,
    }
}

const CardBasicInfo = ({user, userMutation, emailEditable, statusEditable, workgroupsEditable, groupsEditable, notesEditable, canConfirmEmail}) => {
    const {t} = useTranslation(['user', "validation"]);
    const {currentUser} = useCurrentUser();
    const {askConfirmation} = useFeedbackTools();

    const checkCommonWorkgroups = (selectedWorkgroups) => {
        const currentUserWorkgroupIds = currentUser?.data?.workgroups?.map(w => w.id.toString()) || [];
        const selectedWorkgroupIds = selectedWorkgroups.map(w => w.id);
        return selectedWorkgroupIds.some(id => currentUserWorkgroupIds.includes(id.toString()));
    }

    const formik = useFormik({
        initialValues: getFormikValues(user),
        validationSchema: yup.object({
            name: yup
                .string()
                .required(t('validation:required')),
            surname: yup
                .string()
                .required(t('validation:required')),
            email: yup
                .string()
                .email(t('validation:email'))
                .required(t('validation:required')),
            workgroups: yup
                .array()
                .required(t('validation:required')),
            groups: yup
                .array()
                .required(t('validation:required')),
            isActive: yup
                .boolean()
                .required(t('validation:required')),
        }),
        onSubmit: async (values) => {
            const saveUser = async (values, shouldRedirect = false) => {
                let {email, isActive, workgroups, groups, ...data} = values;
                const workgroupIds = workgroups.map(({id}) => id);
                const groupIds = groups.map(({id}) => id);

                if (statusEditable) {
                    data["isActive"] = isActive;
                }
                if (emailEditable) {
                    data["email"] = email;
                }
                if (workgroupsEditable) {
                    data["workgroupIds"] = workgroupIds;
                }
                if (groupsEditable) {
                    data["groupIds"] = groupIds;
                }

                try {
                    const updatedUser = await userMutation.mutateAsync({ ...data, shouldRedirect });
                    if (!shouldRedirect) {
                        formik.resetForm({values: getFormikValues(updatedUser)}); // reset form
                    }
                } catch (e) {
                    // Do nothing because the error is already handled by the FeedbackProvider
                }
            };

            const isCurrentUserAdmin = currentUser?.data?.role === "ADMIN";

            if (!isCurrentUserAdmin && workgroupsEditable && !checkCommonWorkgroups(values.workgroups)) {
                askConfirmation({
                    title: t('cardBasicInfo.confirmSave'),
                    description: t('cardBasicInfo.noCommonWorkgroups'),
                    onConfirm: () => saveUser(values, true)
                });
            } else {
                await saveUser(values);
            }
        }
    });

    const disableSave = !formik.dirty;
    const workgroupIds = formik.values.workgroups?.map(w => w.id) ?? [];

    const isEmailConfirmed = user.isConfirmed;
    const isEmailConfirmationPending = Boolean(user.unconfirmedEmail) && user.unconfirmedEmail !== user.email;
    const showEmailConfirmationButton = (!isEmailConfirmed || isEmailConfirmationPending) && canConfirmEmail;

    const onEmailConfirmed = () => {
        formik.setFieldValue('email', user.unconfirmedEmail);
    }

    return <Card>
        <MDBox py={2} px={2}>
            <MDTypography variant="h5" fontWeight="medium" textTransform="capitalize">
                {t('cardBasicInfo.title')}
            </MDTypography>
            <Stack spacing={2} mt={2} alignItems="center">
                <TextField name="name"
                           label={t('cardBasicInfo.name')}
                           value={formik.values.name}
                           onChange={formik.handleChange}
                           error={formik.touched.name && Boolean(formik.errors.name)}
                           helperText={formik.touched.name && formik.errors.name}
                           variant="standard"
                           fullWidth
                />
                <TextField name="surname"
                           label={t('cardBasicInfo.surname')}
                           value={formik.values.surname}
                           onChange={formik.handleChange}
                           error={formik.touched.name && Boolean(formik.errors.surname)}
                           helperText={formik.touched.name && formik.errors.surname}
                           variant="standard"
                           fullWidth
                />
                <TextField name="email"
                           label={t('cardBasicInfo.email')}
                           value={formik.values.email}
                           onChange={formik.handleChange}
                           error={formik.touched.email && Boolean(formik.errors.email)}
                           helperText={formik.touched.email && formik.errors.email}
                           variant="standard"
                           fullWidth
                           disabled={!emailEditable}
                />
                <Stack direction="row" justifyContent="space-between" flexWrap="wrap" width="100%">
                    {
                        isEmailConfirmed ?
                            <MDTypography variant="caption" color="success" fontWeight="bold">
                                {t('cardBasicInfo.emailConfirmed')}
                            </MDTypography>
                            :
                            <MDTypography variant="caption" color="error" fontWeight="bold">
                                {t('cardBasicInfo.emailNotConfirmed')}
                            </MDTypography>
                    }
                    {
                        isEmailConfirmationPending &&
                        <MDTypography variant="caption" color="info" fontWeight="bold">
                            {t('cardBasicInfo.emailConfirmationPending', {email: user.unconfirmedEmail})}
                        </MDTypography>
                    }
                    {
                        showEmailConfirmationButton && <ConfirmEmailButton userId={user.id} onEmailConfirmed={onEmailConfirmed}/>
                    }
                </Stack>
                {
                    workgroupsEditable &&
                    <WorkgroupsAutocomplete selectedWorkgroups={formik.values.workgroups}
                                            setSelectedWorkgroups={value => formik.setFieldValue('workgroups', value)}
                                            fieldProps={
                                                {
                                                    error: formik.touched.workgroups && Boolean(formik.errors.workgroups),
                                                    helperText: formik.touched.workgroups && formik.errors.workgroups,
                                                }
                                            }
                                            fullWidth/>
                }
                {
                    groupsEditable &&
                    <GroupsAutocomplete workgroupIds={workgroupIds}
                                        selectedGroups={formik.values.groups}
                                        setSelectedGroups={value => formik.setFieldValue('groups', value)}
                                        fieldProps={
                                            {
                                                error: formik.touched.groups && Boolean(formik.errors.groups),
                                                helperText: formik.touched.groups && formik.errors.groups,
                                            }
                                        }
                                        fullWidth/>
                }
                {
                    notesEditable && <TextField name="notes"
                                                label={t('cardBasicInfo.notes')}
                                                value={formik.values.notes ?? ""}
                                                onChange={formik.handleChange}
                                                error={formik.touched.notes && Boolean(formik.errors.notes)}
                                                helperText={formik.touched.notes && formik.errors.notes}
                                                variant="standard"
                                                multiline
                                                rows={4}
                                                fullWidth
                                                disabled={!emailEditable}
                    />
                }
                {
                    statusEditable && <MDBox display="flex" alignItems="center" width="100%" mb={0.5} ml={-1.5}>
                        <MDBox mt={0.5}>
                            <Switch checked={formik.values.isActive} onChange={() => (
                                formik.setFieldValue('isActive', !formik.values.isActive)
                            )}/>
                        </MDBox>
                        <MDBox width="80%" ml={0.5}>
                            <MDTypography variant="button" fontWeight="regular" color="text">
                                {t('cardBasicInfo.enabled')}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                }
                <MDBox>
                    <MDLoadingButton variant="contained"
                                     color="dark"
                                     loading={userMutation.isLoading}
                                     onClick={formik.handleSubmit}
                                     disabled={disableSave}>
                        {t('cardBasicInfo.submit')}
                    </MDLoadingButton>
                </MDBox>
            </Stack>
        </MDBox>
    </Card>
}

export default CardBasicInfo;